"use client";
import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { message, Typography } from "antd";
import api from "../../api/api";
import { useTranslation } from "react-i18next";

const { Title, Paragraph } = Typography;

// Styled Components
const Container = styled.div`
  background-color: #f9f9f9;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 12px 12px 0;
  z-index: 1;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background-color: black;
  border-radius: 8px;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
    border-radius: 8px;
  }
`;

const BlueBox = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 30%;
  width: 100%;
  background-color: #1576ed;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  z-index: 0;
`;

// ✅ Fixed: Properly converts YouTube URL to embed without spaces
const convertToEmbedUrl = (url) => {
  if (!url) return null;

  // Handle youtu.be and youtube.com formats
  const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match
    ? `https://www.youtube.com/embed/${match[1]}?rel=0&showinfo=0&modestbranding=1`
    : null;
};

// Component
const TroubleshootVideo = () => {
  const [videoSrc, setVideoSrc] = useState("");
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false); // Prevents duplicate API calls

  const getGuideVideo = async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    try {
      const response = await api.getGuideVideo();
      const data = response.data;

      if (data?.video_link) {
        const embedUrl = convertToEmbedUrl(data.video_link);
        if (embedUrl) {
          setVideoSrc(embedUrl);
        } else {
          message.error("Invalid YouTube link format.");
        }
      } else {
        message.warning("No video link found.");
      }
    } catch (err) {
      console.error("Failed to fetch guide video:", err);
      message.error("Failed to load the guide video. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGuideVideo();
  }, []);

  const {t} = useTranslation();

  return (
    <Container>
      <ContentWrapper>
        <Title level={4}>{t('having_trouble')}</Title>
        <Paragraph>
          {t('watch_the_following')}
        </Paragraph>

        {loading ? (
          <p style={{ color: "#1576ed", textAlign: "center" }}>
            Loading video...
          </p>
        ) : videoSrc ? (
          <VideoWrapper>
            <iframe
              src={videoSrc}
              title="Guide Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          </VideoWrapper>
        ) : (
          <p style={{ color: "red", textAlign: "center" }}>
            {t('video_unavailable')}
          </p>
        )}
      </ContentWrapper>

      <BlueBox />
    </Container>
  );
};

export default TroubleshootVideo;
